<template>
  <section class="section center">
    <div class="card">
      <h1>Your company account</h1>
      <p class="muted">
        Register your business and start managing your care home.
      </p>

      <!-- Success Message -->
      <div v-if="success" class="success-message">
        <h3>✓ Registration Successful!</h3>
        <p>Your account has been created successfully.</p>
        <p>You can now <RouterLink to="/login">log in</RouterLink> to access your dashboard.</p>
      </div>

      <div v-if="isLogin" class="success-message">
        <h3>You already have an account or an employee</h3>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="error">
        {{ error }}
      </div>

      <!-- Registration Form (hide after success) -->
      <form v-if="!success && !isLogin" @submit.prevent="submitRegistration">
        <label>
          Company name
          <input
            v-model.trim="form.companyName"
            type="text"
            required
          />
        </label>

        <label>
          Admin full name
          <input
            v-model.trim="form.fullName"
            type="text"
            required
          />
        </label>

        <label>
          Admin email
          <input
            v-model.trim="form.email"
            type="email"
            required
          />
        </label>

        <label>
          Password
          <div class="password-field">
            <input
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              required
              @focus="passwordTouched = true"
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
        </label>

        <div v-if="passwordTouched || form.password" class="password-strength">
          <div class="strength-header">
            <span>Password strength</span>
            <strong :class="strengthClass">{{ strengthLabel }}</strong>
          </div>

          <div class="strength-bar">
            <div
              class="strength-fill"
              :class="strengthClass"
              :style="{ width: strengthWidth }"
            ></div>
          </div>

          <p v-if="missingRequirements.length" class="password-hint">
            Add {{ missingRequirements.join(', ') }}.
          </p>

          <p v-else class="password-hint success">
            Password looks strong.
          </p>
        </div>

        <label>
          Confirm password
          <div class="password-field">
            <input
              v-model="form.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              required
            />
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
        </label>

        <p
          v-if="form.confirmPassword && !passwordsMatch"
          class="field-error"
        >
          Passwords do not match.
        </p>

        <label>
          Choose plan
          <select v-model="form.plan" required>
            <option value="starter">Starter — $19/month</option>
            <option value="growth">Growth — $4 per employee/month</option>
          </select>
        </label>

        <button
          class="button"
          type="submit"
          :disabled="loading || !canSubmit"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          {{ loading ? 'Creating account...' : 'Register' }}
        </button>
      </form>

      <p class="small" v-if="!success && !isLogin">
        Already registered?
        <RouterLink to="/login">Log in</RouterLink>
      </p>
    </div>
  </section>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { BASE_URL } from '@/config';
import axios from 'axios';

const route = useRoute()
const router = useRouter()
const API_URL = BASE_URL;

const loading = ref(false)
const error = ref('')
const success = ref(false)
const isLogin = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const passwordTouched = ref(false)

const token = localStorage.getItem('jwt');
if (token) {
      isLogin.value = true;
}

const form = reactive({
  companyName: '',
  fullName: '',
  email: '',
  password: '',
  confirmPassword: '',
  plan: route.query.plan === 'growth' ? 'growth' : 'starter'
})

const passwordRules = computed(() => ({
  length: form.password.length >= 8,
  uppercase: /[A-Z]/.test(form.password),
  lowercase: /[a-z]/.test(form.password),
  number: /[0-9]/.test(form.password),
  special: /[^A-Za-z0-9]/.test(form.password)
}))

const strengthScore = computed(() => {
  return Object.values(passwordRules.value).filter(Boolean).length
})

const strengthLabel = computed(() => {
  if (!form.password) return 'Not started'
  if (strengthScore.value <= 2) return 'Weak'
  if (strengthScore.value <= 4) return 'Medium'
  return 'Strong'
})

const strengthClass = computed(() => {
  return strengthLabel.value.toLowerCase().replace(' ', '-')
})

const strengthWidth = computed(() => {
  return `${strengthScore.value * 20}%`
})

const missingRequirements = computed(() => {
  const missing = []

  if (!passwordRules.value.length) missing.push('8 characters')
  if (!passwordRules.value.uppercase) missing.push('uppercase letter')
  if (!passwordRules.value.lowercase) missing.push('lowercase letter')
  if (!passwordRules.value.number) missing.push('number')
  if (!passwordRules.value.special) missing.push('special character')

  return missing
})

const isStrongPassword = computed(() => {
  return strengthScore.value === 5
})

const passwordsMatch = computed(() => {
  return form.password === form.confirmPassword
})

const canSubmit = computed(() => {
  return (
    form.companyName &&
    form.fullName &&
    form.email &&
    isStrongPassword.value &&
    passwordsMatch.value &&
    form.plan
  )
})

async function submitRegistration() {
  error.value = ''
  success.value = false

  if (!canSubmit.value) {
    error.value = 'Please complete all fields and use a strong matching password.'
    return
  }

  loading.value = true

  try {
    // Register the user in Strapi
    await axios.post(`${API_URL}/api/auth/local/register`, {
      username: form.email,
      email: form.email,
      password: form.password,
      company: form.companyName,
      fullname: form.fullName,
      plan: form.plan
    })

    // Registration successful
    success.value = true
    
    // Redirect to login page after 3 seconds
    setTimeout(() => {
      router.push('/login')
    }, 3000)

  } catch (err) {
    console.error('Registration error:', err.response?.data)
    
    // Handle specific error cases
    if (err.response?.data?.error?.message) {
      error.value = err.response.data.error.message
    } else if (err.response?.status === 400) {
      error.value = 'Email already exists. Please use a different email or log in.'
    } else {
      error.value = 'Registration failed. Please try again.'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.password-field {
  position: relative;
}

.password-field input {
  width: 100%;
  padding-right: 45px;
}

.password-field .btn-outline-secondary {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 5px 10px;
}

.password-field .btn-outline-secondary:hover {
  background: transparent;
  color: #333;
}

.success-message {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
}

.success-message h3 {
  color: #155724;
  margin-bottom: 10px;
}

.success-message p {
  color: #155724;
  margin: 5px 0;
}

.success-message a {
  color: #155724;
  font-weight: bold;
  text-decoration: underline;
}

.error {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  color: #721c24;
}

</style>
